import { useEffect, useState } from 'react'
import {
  loadShareholderRoster,
  setShareholderAccess,
} from '@/features/auth/repository/crmUserRepository'
import { ROLE_LABELS, type CrmUser } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'

type Holder = { id: string; name: string; email: string }

/**
 * The share register, as a list of logins that are off until somebody says so.
 *
 * Shareholders are not people an admin remembers to add. They arrive by the
 * dozen from a Folkeinvest round, and the old screen made every one of them a
 * typed address and a picked link — which meant, in practice, that a holder who
 * asked for access got it and nobody else was ever listed. So the register *is*
 * the list here: every row on the aksjeeierbok appears whether or not it has an
 * account, and the only thing to decide per line is the switch.
 *
 * **Off is the default, and it is an absence rather than a setting.** A holder
 * with no `skidaCrmUsers` document reads as deactivated, so importing a round
 * hands out nothing at all; ticking the box is what creates the account, with
 * the `shareholderId` link the rules read to answer "which record is theirs".
 *
 * A holder who is also something else — a staff member who owns shares, an
 * ambassador who does — gets their roles shown and no switch. Their account is
 * the list above's business: `disabled` is account-wide, so a shareholder
 * toggle on an admin's row would be a button that revokes an admin.
 */
export function ShareholderAccess({ users }: { users: CrmUser[] }) {
  const [roster, setRoster] = useState<Holder[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** The holder id currently being written, so one row at a time is inert. */
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void loadShareholderRoster()
      .then((r) => live && setRoster(r))
      .catch((err: unknown) => {
        if (!live) return
        setRoster([])
        setError(err instanceof Error ? err.message : 'Could not read the register.')
      })
    return () => {
      live = false
    }
  }, [])

  /**
   * Which account belongs to a register row.
   *
   * The explicit `shareholderId` link first — that is the one the rules read,
   * and the one an admin set deliberately. Failing that, an account on the same
   * address that is not already linked to *another* holder: a holder who signed
   * up before anybody linked them should show as the account they are, not as a
   * second row asking to be created.
   */
  const linked = new Map(users.filter((u) => u.shareholderId).map((u) => [u.shareholderId!, u]))
  const accountFor = (h: Holder): CrmUser | undefined =>
    linked.get(h.id) ??
    (h.email
      ? users.find((u) => u.email.trim().toLowerCase() === h.email && !u.shareholderId)
      : undefined)

  const rows = (roster ?? []).map((holder) => ({ holder, account: accountFor(holder) }))

  /**
   * Shareholder logins with no row on the register.
   *
   * They would otherwise be invisible: the groups above skip an off-domain
   * account that holds nothing but `shareholder`, on the understanding that this
   * table covers them. A link pointing at a deleted holder, or an address that
   * matches nothing, must not be the one account nobody can see.
   */
  const placed = new Set(rows.map((r) => r.account?.email).filter(Boolean))
  const unplaced = users.filter((u) => u.roles.includes('shareholder') && !placed.has(u.email))

  const toggle = async (holder: Holder, active: boolean) => {
    setBusy(holder.id)
    setError(null)
    try {
      await setShareholderAccess(holder, active)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change access.')
    } finally {
      setBusy(null)
    }
  }

  const active = rows.filter(
    (r) => r.account && !r.account.disabled && r.account.roles.includes('shareholder'),
  ).length

  return (
    <div className="admincard holderaccess">
      <div className="card-label">
        Shareholders
        {roster && roster.length > 0 && (
          <span className="holderaccess__count">
            {active} of {roster.length} with access
          </span>
        )}
      </div>

      {error && <div className="admin__error">{error}</div>}

      <div className="holderaccess__head">
        <div>Holder</div>
        <div>Email</div>
        <div>Last sign-in</div>
        <div>Access</div>
      </div>

      {roster === null && <div className="empty-note">Loading the register…</div>}

      {rows.map(({ holder, account }) => {
        const other = account?.roles.filter((r) => r !== 'shareholder') ?? []
        const on = !!account && !account.disabled && account.roles.includes('shareholder')
        return (
          <div
            className={`holderaccess__row${on || other.length ? '' : ' holderaccess__row--off'}`}
            key={holder.id}
          >
            <div className="team__name">{holder.name}</div>
            <div className="muted team__email">
              {holder.email || <span className="team__gap">no email on the register</span>}
            </div>
            <div className="muted">
              {account?.lastSignInAt
                ? formatDate(new Date(account.lastSignInAt).toISOString())
                : '—'}
            </div>
            <div>
              {other.length ? (
                // Managed in the list above, and said so by showing what they
                // hold rather than a switch that would reach further than it
                // looks.
                other.map((r) => (
                  <span className={`pill team__role team__role--${r}`} key={r}>
                    {ROLE_LABELS[r]}
                  </span>
                ))
              ) : (
                <label className="holderaccess__switch">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!holder.email || busy === holder.id}
                    title={
                      holder.email
                        ? undefined
                        : 'The address is the account. Put an email on the register row first.'
                    }
                    onChange={(e) => void toggle(holder, e.target.checked)}
                  />
                  {on ? 'Active' : 'Off'}
                </label>
              )}
            </div>
          </div>
        )
      })}

      {roster?.length === 0 && !error && (
        <div className="empty-note">The register is empty — nobody holds shares yet.</div>
      )}

      {unplaced.map((u) => (
        <div className="holderaccess__row" key={u.email}>
          <div className="team__name">
            {u.displayName}
            <span className="team__gap holderaccess__stray">not on the register</span>
          </div>
          <div className="muted team__email">{u.email}</div>
          <div className="muted">
            {u.lastSignInAt ? formatDate(new Date(u.lastSignInAt).toISOString()) : '—'}
          </div>
          <div>
            <span className="pill team__role team__role--shareholder">
              {ROLE_LABELS.shareholder}
            </span>
          </div>
        </div>
      ))}

      <div className="micro admincard__note">
        Every holder on the aksjeeierbok is listed here, and every one starts without access. A
        shareholder login sees their own holding and the cap table — name, shares and percentage —
        and never the register itself.
      </div>
    </div>
  )
}
